// Ayuda de escritura para las plantillas ({{dato}}, {{dato | método}}, {{= código }}): qué se está escribiendo donde está el cursor y
// qué sugerir. Sin dependencias del editor (CodeMirror solo la usa), para poder probarla. Las ayudas de código deben coincidir con
// PRELUDE de agent-runtime/src/tools/js.ts y los métodos con METHODS / PIPES.
/* eslint-disable no-template-curly-in-string -- ${…} son los huecos de los snippets de CodeMirror, no plantillas de JS */
import { scan } from './templatePreview';
import { METHODS } from './toolDefinition';

const ID = '[\\p{L}_$][\\p{L}\\p{N}_$]*';
const MEMBER_RE = new RegExp(`((?:${ID}(?:\\(\\)|\\[\\d+\\])*\\.)*)(${ID})?$`, 'u');

/**
 * Dónde está el cursor dentro de una plantilla:
 *  - { kind: 'path', from, word }            {{contacto.no|}}  → rutas de datos
 *  - { kind: 'pipe', from, word }            {{x | may|}}      → métodos
 *  - { kind: 'code', from, word, obj }       {{= contacto.| }} → raíces, ayudas y miembros de `obj`
 *  - null fuera de una plantilla (o dentro de un texto entre comillas del código).
 */
export function templateContext(text, pos) {
  text = String(text || '');
  let base = -1;
  for (const t of scan(text)) if (t.start + 2 <= pos && pos <= t.end - 2) { base = t.start + 2; break; }
  if (base < 0) {
    const before = text.slice(0, pos); const open = before.lastIndexOf('{{');
    if (open < 0 || before.slice(open + 2).includes('}}')) return null;
    base = open + 2;
  }
  const inner = text.slice(base, pos);
  const lead = inner.length - inner.trimStart().length;
  if (inner.slice(lead).startsWith('=')) {
    const code = inner.slice(lead + 1);
    if (insideString(code)) return null;
    const m = MEMBER_RE.exec(code);
    const word = m?.[2] || '';
    const obj = (m?.[1] || '').replace(/\.$/, '').replace(/\[(\d+)\]/g, '.$1');
    // «5.» o «x.y» después de un número no son miembros
    if (obj && /^\d/.test(obj)) return null;
    return { kind: 'code', from: pos - word.length, word, obj };
  }
  const bar = lastBar(inner);
  if (bar >= 0) {
    const after = inner.slice(bar + 1); const word = after.trimStart();
    if (word.includes(':') || /\s/.test(word)) return null;
    return { kind: 'pipe', from: pos - word.length, word };
  }
  const word = inner.slice(lead);
  if (/\s/.test(word)) return null;
  return { kind: 'path', from: base + lead, word };
}

function insideString(code) {
  let q = null;
  for (let i = 0; i < code.length; i++) {
    const ch = code[i];
    if (q) { if (ch === '\\') i++; else if (ch === q) q = null; }
    else if (ch === '"' || ch === "'" || ch === '`') q = ch;
  }
  return q !== null;
}
function lastBar(s) {
  let q = null; let at = -1;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (q) { if (ch === q) q = null; continue; }
    if (ch === '"' || ch === "'") q = ch; else if (ch === '|') at = i;
  }
  return at;
}

/** Ayudas que el motor pone en todo código {{= }} (hora de México). `snippet`: ${…} son los huecos que se llenan con Tab. */
export const JS_HELPERS = [
  { label: 'fechaMx', snippet: 'fechaMx(${})', detail: 'fecha de México por partes', info: 'fechaMx() o fechaMx(fecha) → { año, mes, dia, hora, minuto, diaSemana, nombreMes, iso }.\nEj.: fechaMx().diaSemana → "viernes"' },
  { label: 'formatoFecha', snippet: 'formatoFecha(${ahora}, "${larga}")', detail: 'fecha como texto', info: 'formatoFecha(fecha, "corta" | "larga" | "hora")\n"corta" → 09/10/2026 · "larga" → 9 de octubre de 2026 · "hora" → 09/10/2026 12:30' },
  { label: 'sumarDias', snippet: 'sumarDias(${ahora}, ${1})', detail: 'mover una fecha N días', info: 'sumarDias(fecha, n) → fecha ISO. Usa negativos para restar.\nEj.: formatoFecha(sumarDias(ahora, 1), "larga") → mañana' },
  { label: 'pesos', snippet: 'pesos(${n})', detail: 'número como pesos', info: 'pesos(1234.5) → "$1,234.50"' },
];
const FECHA_FIELDS = [
  ['año', 'número, ej. 2026'], ['mes', '1 a 12'], ['dia', '1 a 31'], ['hora', '0 a 23'], ['minuto', '0 a 59'],
  ['diaSemana', 'lunes, martes…'], ['nombreMes', 'enero, febrero…'], ['iso', 'fecha ISO'],
];
const JS_GLOBALS = [
  { label: 'Math.round', snippet: 'Math.round(${x})', detail: 'redondear' },
  { label: 'Math.max', snippet: 'Math.max(${a}, ${b})', detail: 'el mayor' },
  { label: 'Math.min', snippet: 'Math.min(${a}, ${b})', detail: 'el menor' },
  { label: 'Number', snippet: 'Number(${x})', detail: 'convertir a número' },
  { label: 'String', snippet: 'String(${x})', detail: 'convertir a texto' },
  { label: 'JSON.stringify', snippet: 'JSON.stringify(${x})', detail: 'como JSON' },
  { label: 'new Date', snippet: 'new Date(${})', detail: 'fecha (UTC)' },
];
const JS_KEYWORDS = ['return', 'const', 'let', 'if', 'else', 'true', 'false', 'null'];
/** Métodos comunes de texto y listas (sin saber el tipo exacto se ofrecen los dos grupos). */
const JS_MEMBERS = [
  { label: 'length', detail: 'largo (texto o lista)', type: 'property' },
  { label: 'split', snippet: 'split("${ }")', detail: 'texto → lista' },
  { label: 'toUpperCase', snippet: 'toUpperCase()', detail: 'texto en mayúsculas' },
  { label: 'toLowerCase', snippet: 'toLowerCase()', detail: 'texto en minúsculas' },
  { label: 'trim', snippet: 'trim()', detail: 'quitar espacios de las orillas' },
  { label: 'includes', snippet: 'includes(${"x"})', detail: '¿contiene? (texto o lista)' },
  { label: 'startsWith', snippet: 'startsWith(${"x"})', detail: '¿empieza con?' },
  { label: 'replace', snippet: 'replace(${"a"}, ${"b"})', detail: 'cambiar un texto' },
  { label: 'slice', snippet: 'slice(${0}, ${10})', detail: 'una parte (texto o lista)' },
  { label: 'padStart', snippet: 'padStart(${2}, "${0}")', detail: 'rellenar a la izquierda' },
  { label: 'map', snippet: 'map((x) => ${x})', detail: 'lista → otra lista' },
  { label: 'filter', snippet: 'filter((x) => ${x})', detail: 'solo los que cumplen' },
  { label: 'find', snippet: 'find((x) => ${x})', detail: 'el primero que cumple' },
  { label: 'some', snippet: 'some((x) => ${x})', detail: '¿alguno cumple?' },
  { label: 'join', snippet: 'join("${, }")', detail: 'lista → texto' },
  { label: 'toFixed', snippet: 'toFixed(${2})', detail: 'número con N decimales' },
];

const childrenOf = (vars, obj) => {
  const seen = new Map(); const prefix = `${obj}.`;
  for (const v of vars) {
    if (!v.path.startsWith(prefix)) continue;
    const seg = v.path.slice(prefix.length).split('.')[0];
    if (!seg || seen.has(seg)) continue;
    const exact = v.path === prefix + seg ? v : null;
    seen.set(seg, { seg, detail: exact?.label !== seg ? exact?.label : undefined, hint: exact?.hint });
  }
  return [...seen.values()];
};

/**
 * Sugerencias para un contexto: [{ label, detail?, info?, type, apply?, snippet?, replaceDot?, boost? }].
 * `vars`: [{ path, label, hint? }] (los datos que existen en ese campo). `replaceDot`: la sugerencia reemplaza también el «.» anterior
 * (posiciones de lista: `data.` → `data[0]`).
 */
export function suggestions(ctx, vars = []) {
  if (!ctx) return [];
  if (ctx.kind === 'path') return vars.map((v) => ({ label: v.path, detail: v.label && v.label !== v.path.split('.').pop() ? v.label : undefined, info: v.hint !== undefined && v.hint !== '' ? `Ejemplo: ${v.hint}` : undefined, type: 'variable' }));
  if (ctx.kind === 'pipe') return METHODS.map((m) => ({ label: m.name, apply: m.arg ? `${m.name}:${m.arg}` : m.name, detail: m.label, info: m.example, type: 'function' }));
  if (ctx.obj) {
    if (/^fechaMx\(\)$/.test(ctx.obj)) return FECHA_FIELDS.map(([label, detail]) => ({ label, detail, type: 'property', boost: 2 }));
    const own = childrenOf(vars, ctx.obj).map((c) => (/^\d+$/.test(c.seg)
      ? { label: `[${c.seg}]`, replaceDot: true, detail: c.detail || 'posición en la lista', type: 'property', boost: 3 }
      : { label: c.seg, detail: c.detail, info: c.hint !== undefined && c.hint !== '' ? `Ejemplo: ${c.hint}` : undefined, type: 'property', boost: 3 }));
    return [...own, ...JS_MEMBERS.map((m) => ({ type: 'method', ...m, boost: -1 }))];
  }
  const roots = [...new Set(vars.map((v) => v.path.split('.')[0]))].map((r) => ({ label: r, detail: vars.find((v) => v.path === r)?.label || 'dato', type: 'variable', boost: 3 }));
  return [
    ...roots,
    ...JS_HELPERS.map((h) => ({ ...h, type: 'function', boost: 2 })),
    ...JS_GLOBALS.map((g) => ({ ...g, type: 'function' })),
    ...JS_KEYWORDS.map((k) => ({ label: k, type: 'keyword', boost: -2 })),
  ];
}

const ROOT_LABELS = { contacto: 'Datos de la persona', agente: 'Datos del agente', datos: 'Datos previos', entrada: 'Datos de entrada', nodos: 'Salida de los pasos' };
/** Lista de datos (vars) a partir de un objeto de ejemplo: { contacto: { nombre: 'Ana' } } → contacto, contacto.nombre. */
export function varsFromSample(sample, group = 'Datos', max = 120) {
  const out = [];
  const walk = (v, path, depth) => {
    if (out.length >= max) return;
    const leaf = v === null || typeof v !== 'object';
    out.push({ path, label: ROOT_LABELS[path] || path.split('.').pop(), hint: leaf ? String(v ?? '').slice(0, 60) : Array.isArray(v) ? `${v.length} elementos` : undefined, group });
    if (leaf || depth >= 6) return;
    const entries = Array.isArray(v) ? v.slice(0, 3).map((x, i) => [String(i), x]) : Object.entries(v);
    for (const [k, x] of entries) walk(x, `${path}.${k}`, depth + 1);
  };
  for (const [k, v] of Object.entries(sample || {})) walk(v, k, 0);
  return out;
}
